import { AbstractControl, FormGroup } from "@angular/forms";

import _cloneDeep from "lodash/cloneDeep";
import { Observable, Subject, Subscription } from "rxjs";
import { merge } from "rxjs/internal/observable/merge";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import { DisposeMixin, EmptyClassInstance } from "../mixins/dispose.mixin";

export abstract class BaseObservableForm<TFormModel> extends DisposeMixin(EmptyClassInstance) {
    protected constructor(
        protected formInstance: FormGroup,
        protected initialFormState: TFormModel
    ) {
        super();

        this.attachOnChangeListener();

        merge(
            this.onFormChanged,
            this.onFormSubmitted
        ).subscribe(model => {
            this.formInstance.patchValue(model, { emitEvent: false });
            this.initialFormState = model;
        });
    }

    private _onChangeSubscription: Subscription;

    private _onFormChanged = new Subject<TFormModel>();
    private _onFormSubmitted = new Subject<TFormModel>();

    public get onFormChanged(): Observable<TFormModel> {
        return this._onFormChanged.pipe(
            debounceTime(250),
            distinctUntilChanged()
        );
    }

    public get onFormSubmitted(): Observable<TFormModel> {
        return this._onFormSubmitted.pipe(
            debounceTime(250)
        );
    }

    public get formStatus(): string {
        return this.formInstance.status;
    }

    public get formValue(): any {
        return _cloneDeep(this.formInstance.value);
    }

    public getFormControl(alias: string): AbstractControl {
        return this.formInstance.get(alias);
    }

    public dispose(): void {
        super.dispose();
        this.detachOnChangeListener();
    }

    public submit(): void {
        if (this.isFormValid()) {
            this._onFormSubmitted.next(this.getFormModel());
        }
    }

    public abstract reset(): void;

    protected abstract bindOnChangeSubscription(): Observable<TFormModel>;

    protected getFormModel(partialFormModel: any = {}): TFormModel {
        return _cloneDeep({ ...this.formValue, ...partialFormModel }) as TFormModel;
    }

    protected isFormValid(): boolean {
        return this.formInstance.valid;
    }

    protected attachOnChangeListener(): void {
        this.detachOnChangeListener();

        const onChangeSubscription = this.bindOnChangeSubscription();

        if (onChangeSubscription) {
            this._onChangeSubscription = onChangeSubscription.subscribe(this._onFormChanged);
        }
    }

    protected detachOnChangeListener(): void {
        if (this._onChangeSubscription) {
            this._onChangeSubscription.unsubscribe();
            this._onChangeSubscription = null;
        }
    }
}
